import { useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

export interface MultiSelectOption {
  value: string;
  label: string;
  hint?: string;
}

interface MultiSelectProps {
  /** Items to choose from. Pass `string[]` (value === label) or `{value, label, hint?}[]`. */
  options: ReadonlyArray<string | MultiSelectOption>;
  /** Currently selected values. */
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Show "+ Přidat" input to add custom values not in `options`. */
  allowCustom?: boolean;
  loading?: boolean;
  badgeBorderClass?: string;
  className?: string;
  emptyLabel?: string;
}

interface InternalItem {
  value: string;
  label: string;
  hint?: string;
  isCustom: boolean;
}

const normalize = (opts: ReadonlyArray<string | MultiSelectOption>): MultiSelectOption[] =>
  opts.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

/** Lowercase + strip diacritics (Táckárna → tackarna) for accent/case-insensitive search. */
const foldString = (s: string): string =>
  s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Vyber…',
  allowCustom = false,
  loading = false,
  badgeBorderClass,
  className,
  emptyLabel = 'Žádné položky',
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [custom, setCustom] = useState('');

  const normalizedOptions = useMemo(() => normalize(options), [options]);

  // Display list = options ∪ selected (so custom-added values stay visible).
  const allItems = useMemo<InternalItem[]>(() => {
    const seen = new Set<string>();
    const list: InternalItem[] = [];
    for (const o of normalizedOptions) {
      if (!seen.has(o.value)) {
        seen.add(o.value);
        list.push({ ...o, isCustom: false });
      }
    }
    for (const v of value) {
      if (!seen.has(v)) {
        seen.add(v);
        list.push({ value: v, label: v, isCustom: true });
      }
    }
    return list;
  }, [normalizedOptions, value]);

  // Map for fast value→label lookup (used by chips).
  const labelByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of allItems) m.set(i.value, i.label);
    return m;
  }, [allItems]);

  const filtered = useMemo(() => {
    const q = foldString(search.trim());
    if (q === '') return allItems;
    return allItems.filter((o) => foldString(o.label).includes(q) || foldString(o.value).includes(q));
  }, [allItems, search]);

  const toggle = (item: string) => {
    onChange(value.includes(item) ? value.filter((v) => v !== item) : [...value, item]);
  };

  const remove = (item: string) => {
    onChange(value.filter((v) => v !== item));
  };

  const addCustom = (e?: FormEvent) => {
    e?.preventDefault();
    const v = custom.trim();
    if (v === '' || value.includes(v)) {
      setCustom('');
      return;
    }
    onChange([...value, v]);
    setCustom('');
  };

  const onCustomKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustom();
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => {
            const label = labelByValue.get(v) ?? v;
            const isCustom = !normalizedOptions.some((o) => o.value === v);
            return (
              <Badge
                key={v}
                variant="outline"
                className={cn('gap-1 text-xs', badgeBorderClass, isCustom && 'bg-muted/50')}
              >
                {label}
                <button
                  type="button"
                  onClick={() => remove(v)}
                  className="hover:text-destructive cursor-pointer"
                  aria-label={`Odebrat ${label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Trigger */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          className={cn(
            'inline-flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-sm shadow-xs',
            'hover:bg-accent/40 cursor-pointer',
          )}
        >
          <span className="text-muted-foreground">
            {value.length === 0 ? placeholder : `Vybráno: ${value.length}`}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </PopoverTrigger>

        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width,300px)] p-0">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Hledat…"
                className="pl-7 h-8 text-sm"
              />
            </div>
          </div>

          <div className="max-h-[220px] overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center gap-2 justify-center text-xs text-muted-foreground py-6">
                <Spinner size="sm" />
                <span>Načítám…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-xs text-muted-foreground px-3 py-4 text-center">
                {normalizedOptions.length === 0 ? emptyLabel : 'Žádný výsledek pro hledaný text'}
              </div>
            ) : (
              filtered.map((opt) => {
                const checked = value.includes(opt.value);
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left',
                      'hover:bg-accent/60 cursor-pointer',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        checked ? 'bg-primary border-primary text-primary-foreground' : 'border-input',
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.hint && <span className="text-[10px] text-muted-foreground">{opt.hint}</span>}
                    {opt.isCustom && (
                      <span className="text-[10px] text-muted-foreground italic">vlastní</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {allowCustom && (
            <div className="p-2 border-t border-border flex gap-2">
              <Input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                onKeyDown={onCustomKeyDown}
                placeholder="Vlastní hodnota"
                className="h-8 text-sm flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addCustom()}
                disabled={custom.trim() === ''}
                className="h-8 px-2"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
